import React, { Fragment, useState, useRef, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { GIButton } from 'app-components';
import ModalHeader from '@common-components/headers/ModalHeader';
import UniversalInput from '@lib/wrappers/UniversalInput';
import Form from '@lib/wrappers/Form';
import ValidationErrorMessage from '@components/ssap/notifications/ValidationErrorMessage';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { dashboardModalTypes } from '@constants/individualPortal/dashboard/modals/dashboardModalTypes';
import { commonModalTypes as ModalTypes } from '@constants/common/modals/commonModalTypes';
import { environmentSelector } from '@selectors/commonSelectors';
import { executeCallbacks } from '@utils/miscellaneous/executeCallbacks';
import { preventerTools } from '@utils/tools/preventerTools';
import { phoneNumberTools } from '@utils/tools/phoneNumberTools';
import PinInput from '@components/common/form/PinInput';
import { ChannelType } from '@components/broker/types/selfDesignationOtpTypes';

const OTP_LENGTH = 6;

const ContactChangeModal = (props) => {
    const {
        actions: { showModal, otpSend, otpVerify, checkEmailAvailability },
        modal: {
            modalData: {
                data,
                name,
                value: defaultValue,
                type,
                validationType,
                updateData,
                callbackOnClose,
                isSMSValidation = false
            }
        },
        methods,
        modalHeaderProps,
        ssapForm,
    } = props;

    const { encryptedHouseholdId, impersonation, impersonationDetails } = useSelector(environmentSelector);
    const [otpStep, setOtpStep] = useState(!type);
    const [value, setValue] = useState(defaultValue ?? data[name] ?? '');
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
    const [otpError, setOtpError] = useState('');
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [trackedValues, tracker] = useState(null);
    const [emailError, setEmailError] = useState('');

    const isPhone = name === 'phoneNumber';
    const channel = isPhone ? ChannelType.SMS : ChannelType.EMAIL;
    const contactKey = isPhone ? 'phone' : 'email';
    const formattedValue = isPhone ? phoneNumberTools.getAdjustedPhoneNumber(value) : value;
    const form_name = 'contactChangeForm';
    const otp_form_name = 'otpVerifyForm';
    const SUBMIT_CLICK = 'onFormSubmit';
    const OTP_SUCCESS = 'modals.body.otp_success';
    const OTP_VERIFY_FAILED = 'modals.body.otp_verify_fail';
    const OTP_SEND_FAILED = 'modals.body.otp_send_fail';

    const intlApi = useIntl();
    const contactValue = intlApi.formatMessage({ id: isPhone ? 'ssap.input.label.phone_number' : 'ssap.input.label.email' },);
    const resendViaCallText = isSMSValidation ? '' : intlApi.formatMessage({ id: 'modals.body.resend_via_call', defaultMessage: ' ' }).trim();
    const resendViaSmsText = intlApi.formatMessage({ id: 'modals.body.resend_via_sms', defaultMessage: ' ' }).trim();

    const {
        isEligiblyForSave
    } = preventerTools.provideObject(ssapForm[form_name]);

    const handleModalClose = () => {
        executeCallbacks(callbackOnClose);
        modalHeaderProps.hideModal();
    };

    const handleFailureModal = (modalData = {}) => {
        showModal({
            modalType: dashboardModalTypes.FAILURE_MODAL,
            modalData
        });
    };

    const handleSuccessModal = (callback, saveResult) => {
        showModal({
            modalType: ModalTypes.SAVE_RESULT_MODAL,
            modalData: {
                callback,
                saveResult,
            },
        });
    };


    const onContactChange = (...args) => {
        const [, , , [, value]] = args;
        setValue(value);
        setEmailError('');
    };

    const sendOTP = async (payload) => {
        const res = await otpSend(payload);
        return ({
            errorMessage: res?.message,
            success: res?.success || res?.status === 'SUCCESS',
        });
    };

    const verifyOTP = async (updatedData, successMessage) => {
        const res = await otpVerify(updatedData);
        return ({
            errorMessage: res?.message,
            success: res?.success || res?.status === 'SUCCESS',
            successMessage
        });
    };

    useEffect(() => {
        if (otpStep) {
            onOTPSend({});
        }
    }, []);

    const onOTPSend = async ({ viaCall = false }) => {
        if (value) {
            const saveResult = await sendOTP({
                householdId: !impersonation ? encryptedHouseholdId : impersonationDetails.encryptedHouseholdId,
                channel: viaCall ? ChannelType.CALL : channel,
                [contactKey]: value,
            });

            if (saveResult) {
                saveResult.success && setOtpStep(true);
                !saveResult.success && handleFailureModal({ failureMessage: saveResult.errorMessage ?? OTP_SEND_FAILED });
            } else {
                handleFailureModal({ failureMessage: OTP_SEND_FAILED });
            }
        }
    };

    const onContactSubmit = async () => {
        type && methods[form_name][SUBMIT_CLICK]();
        if (!isEligiblyForSave || defaultValue === value) return;
        if (!isPhone && checkEmailAvailability) {
            const isAvailable = await checkEmailAvailability(value);
            if (isAvailable === false) {
                setEmailError(VALIDATION_ERROR_MESSAGES.EMAIL_DUPLICATE);
                return;
            }
            if (isAvailable !== true) {
                handleFailureModal();
                return;
            }
            setEmailError('');
        }
        onOTPSend({});
    };

    const onOTPVerify = async () => {
        const verifyResult = await verifyOTP({
            householdId: !impersonation ? encryptedHouseholdId : impersonationDetails.encryptedHouseholdId,
            otp: otp.join(''),
            [contactKey]: value
        }, OTP_SUCCESS);

        if (verifyResult?.success) {
            handleSuccessModal(handleModalClose, verifyResult);
            updateData({ ...data, [name]: value }, name, value);
        } else {
            setOtpError(OTP_VERIFY_FAILED);
        }
    };

    const checkIfInvalid = () => {
        if(trackedValues === null) {
            return { invalid: true };                    // Button is disabled until User starts changing the input
        }

        const { valid, value } = trackedValues;

        switch (true) {
            case !value:                    // Button is disabled if the value is empty
            case valid === false:           // Button is disabled until we get valid value from input
                return ({ invalid: true, errorKey: VALIDATION_ERROR_MESSAGES[validationType] });
            case value === defaultValue:    // Button is disabled if the value after changes becomes the same as the default value
                return ({ invalid: true }); //Need to confirm message from PM 
            default:
                return ({ invalid: false });
        }
    };

    const ResendButton = ({ viaCall = false, children }) => (
        <span
            data-test-id={`resend-otp-btn${viaCall ? '-via-call' : ''}`}
            className="btn-link pointer"
            onClick={() => onOTPSend({ viaCall })}
        >
            {children}
        </span>
    );

    const updateContactStep = () => {
        return (
            <div>
                <div className='gi-modal__body' id={`${modalHeaderProps.modalId}_body`}>
                    <FormattedMessage id='modals.body.contact_change' values={{ contact: contactValue.toLowerCase() }} />

                    <div className="gi-modal__input" >
                        <Form
                            {...{
                                formName: form_name,
                                formClasses: ['d-flex', 'flex-column-reverse'],
                                onLocalFormSubmit: () => { }
                            }}>
                            <UniversalInput
                                label={{
                                    text: name === 'phoneNumber' ? 'ssap.input.label.new_phone_number' : 'ssap.input.label.new_email',
                                    labelClasses: ['p-0', 'text-semibold', 'text-left-i']
                                }}
                                errorClasses={['m-0']}
                                inputData={{
                                    isInputRequired: true,
                                    type,
                                    name,
                                    helperClasses: ['center-flex', 'align-content-start', 'align-items-start'],
                                    value,
                                    placeholder: name === 'phoneNumber' ? 'ssap.input.placeholder.phone_number' : 'ssap.input.placeholder.email',
                                    validationType,
                                    currentData: {},
                                    fields: []
                                }}
                                inputActions={{
                                    onFieldChange: onContactChange,
                                    tracker
                                }}
                            />
                        </Form>
                        <ValidationErrorMessage
                            isVisible={checkIfInvalid().errorKey}
                            messageText={checkIfInvalid().errorKey}
                            errorClasses={['m-0']}
                        />
                        {emailError && (
                            <ValidationErrorMessage
                                isVisible={true}
                                messageText={emailError}
                                errorClasses={['m-0']}
                            />
                        )}
                    </div>
                </div>
                <div className='gi-modal__footer'>
                    <div className='grid-row controls button__container justify-content-between'>
                        <GIButton
                            id='cancel_button'
                            labelId={'button.cancel'}
                            onClickHandler={handleModalClose}
                            addlClassNames={'gi-button__outline'}
                        />
                        <GIButton
                            id='otp_send_button'
                            labelId={'button.continue'}
                            onClickHandler={onContactSubmit}
                            isDisabled={checkIfInvalid().invalid}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const validateOTPStep = () => {
        return (
            <div>
                <div className="gi-modal__body" id={`${modalHeaderProps.modalId}_body`}>
                    <p className='mt-0'>
                        <FormattedMessage id='modals.body.verify_otp' values={{ value: formattedValue, contact: contactValue.toLowerCase() }} />
                    </p>
                    <label className='text-semibold'>
                        <FormattedMessage id='ssap.input.label.otp' />
                    </label>

                    <Form formName={otp_form_name} formProps={{ onSubmit: (e) => e.preventDefault() }}>
                        <div className='display--flex'>
                            <PinInput {...{otp, setOtp, otpError, setOtpError}} />
                            <GIButton
                                id='otp_verify_button'
                                labelId={'button.submit'}
                                onClickHandler={onOTPVerify}
                                isDisabled={otp.join('').length < OTP_LENGTH || !!otpError}
                            />
                        </div>
                    </Form>

                    {otpError && <span className='text-red'>
                        <FormattedMessage id={otpError} />
                    </span>}

                    <p>
                        <FormattedMessage id='modals.body.no_otp_received' /><span> </span>
                        {(isPhone && resendViaCallText && resendViaSmsText)
                            ? <>
                                <br /><ResendButton>{resendViaSmsText}</ResendButton>
                                <br /><ResendButton viaCall={true}>{resendViaCallText}</ResendButton>
                            </>
                            : <ResendButton><FormattedMessage id={'modals.body.resend'} /></ResendButton>
                        }
                    </p>
                </div>
                <div className='gi-modal__footer'>
                    <div className={`grid-row controls button__container ${type ? 'justify-content-between' : ''}`}>
                        {type && <GIButton
                            id='back_button'
                            labelId={'button.back'}
                            onClickHandler={() => setOtpStep(false)}
                        />}
                        <GIButton
                            id='cancel_button'
                            labelId={'button.cancel'}
                            onClickHandler={handleModalClose}
                            addlClassNames={'gi-button__outline'}
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Fragment >
            <div className='gi-modal__header__head ' >
                <ModalHeader
                    {...{
                        ...modalHeaderProps,
                        hideModalCallback: handleModalClose,
                        modalHeaderTextData: [
                            modalHeaderProps.modalHeaderTextData,
                            { value: otpStep ? formattedValue : undefined }
                        ]
                    } }
                />
            </div>
            {
                !otpStep ? updateContactStep() : validateOTPStep()
            }
        </Fragment >
    );
};

export default ContactChangeModal;